import { assign, fromPromise, setup } from 'xstate5';

import { hashObject } from '@sg/garnish';

import {
  checkIfCacheEntryExists,
  readFromCacheOrFail,
  removeExpiredCacheEntries,
  removeLegacyCacheEntries,
  writeToCacheOrFail,
} from './services';
import { extractContentTypeEntryData } from './utils';

import type {
  ContentfulContentTypeEntries,
  ContentfulContentTypeEntriesMachineContext,
  ContentfulContentTypeEntriesMachineEvents,
  ContentfulContentTypeEntriesMachineInput,
} from './contentful-content-type-entries-machine.types';
import type { EntryCollection } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A state machine to fetch the data from Contentful with caching support.
 */
export function createContentfulContentTypeEntriesMachine<Fields>() {
  return setup({
    types: {
      context: {} as ContentfulContentTypeEntriesMachineContext<Fields>,
      events: {} as ContentfulContentTypeEntriesMachineEvents,
      input: {} as ContentfulContentTypeEntriesMachineInput,
    },
    actors: {
      getEntries: fromPromise<EntryCollection<Fields> | undefined>(async () => {
        throw new Error('`getEntries` actor is not provided.');
      }),
      checkIfCacheEntryExists: fromPromise<boolean, string | undefined>(async ({ input }) =>
        checkIfCacheEntryExists(input),
      ),
      readFromCacheOrFail: fromPromise<ContentfulContentTypeEntries<Fields>, string | undefined>(
        async ({ input }) => readFromCacheOrFail<Fields>(input),
      ),
      writeToCacheOrFail: fromPromise<void, WriteToCacheOrFailInput<Fields>>(async ({ input }) =>
        writeToCacheOrFail<Fields>(input),
      ),
    },
    actions: {
      setInput: assign(({ event }) => {
        const hash = hashObject(event.input.query, { shouldSortKeys: true });
        const cacheEntryId = `${hash}`;

        return {
          input: event.input,
          _cacheEntryId: cacheEntryId,
        };
      }),
      setDataFromCache: assign((_, data: ContentfulContentTypeEntries<Fields>) => ({ data })),
      setData: assign((_, entryCollection: EntryCollection<Fields> | undefined) => {
        const entries = entryCollection?.items ?? [];

        return {
          data: entries.map(extractContentTypeEntryData),
        };
      }),
      setError: assign((_, params: SetErrorParams) => ({ error: params.error })),
      clearError: assign({ error: undefined }),
    },
    guards: {
      checkIfShouldReadFromCache: ({ context, event }) =>
        !context.isPreviewMode && event.input.ttl > 0,
      checkIfShouldWriteToCache: ({ context }) =>
        !context.isPreviewMode && (context.input?.ttl ?? 0) > 0,
      checkIfCacheEntryFound: (_, hasCacheEntry: boolean) => hasCacheEntry,
    },
  }).createMachine({
    context: ({ input }) => ({
      data: undefined,
      error: input.error,
      input: {
        query: undefined,
        ttl: undefined,
      },
      _cacheEntryId: undefined,
      isPreviewMode: input.isPreviewMode ?? false,
    }),

    initial: 'waiting',
    on: {
      GET_ENTRIES: [
        {
          target: '.searching',
          guard: 'checkIfShouldReadFromCache',
          actions: ['clearError', 'setInput'],
        },
        {
          target: '.fetching',
          actions: ['clearError', 'setInput'],
        },
      ],
    },
    states: {
      waiting: {},
      idle: {},
      searching: {
        invoke: {
          src: 'checkIfCacheEntryExists',
          input: ({ context }) => context._cacheEntryId,
          onDone: [
            {
              target: 'reading',
              guard: { type: 'checkIfCacheEntryFound', params: ({ event }) => event.output },
            },
            {
              target: 'fetching',
            },
          ],
          onError: 'fetching',
        },
      },
      reading: {
        invoke: {
          src: 'readFromCacheOrFail',
          input: ({ context }) => context._cacheEntryId,
          onDone: {
            target: 'idle',
            actions: { type: 'setDataFromCache', params: ({ event }) => event.output },
          },
          onError: 'fetching',
        },
      },
      fetching: {
        invoke: {
          src: 'getEntries',
          input: ({ context }) => context.input.query,
          onDone: [
            {
              target: 'writing',
              guard: 'checkIfShouldWriteToCache',
              actions: { type: 'setData', params: ({ event }) => event.output },
            },
            {
              target: 'idle',
              actions: { type: 'setData', params: ({ event }) => event.output },
            },
          ],
          onError: {
            target: 'idle',
            actions: { type: 'setError', params: ({ event }) => ({ error: event.error }) },
          },
        },
      },
      writing: {
        invoke: {
          src: 'writeToCacheOrFail',
          input: ({ context }) => ({
            data: context.data,
            queryHash: context._cacheEntryId,
            ttl: context.input.ttl,
          }),
          onDone: 'idle',
          onError: 'idle',
        },
      },
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────

// We need to remove expired/invalid cache entries as soon as possible.
void removeExpiredCacheEntries();
void removeLegacyCacheEntries();

// ─── Types ───────────────────────────────────────────────────────────────────

type WriteToCacheOrFailInput<Fields> = Parameters<typeof writeToCacheOrFail<Fields>>[0];

type SetErrorParams = {
  error: unknown;
};
